import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq, gt } from 'ember-truth-helpers';
import TableDrawer from 'garaje/components/table-drawer';

interface DetailEvent {
  eventId: string;
  processedDateDisplay: string;
  processedTimeDisplay: string;
  integrationName: string;
  locationName: string | null;
  operationType: string;
  operationTypeLabel: string;
  operationDefinition: string | null;
  operationStatus: string;
  failureType: string | null;
  failureReason: string | null;
  displayReason: string | null;
  errorDetail: string | null;
  recommendedAction: string | null;
  helpCenterUrl: string | null;
  responsibleParty: string | null;
  direction: string | null;
  endpoint: string | null;
  durationMillis: number;
  primaryMetric: string | null;
  correlationId: string | null;
}

interface HealthDashboardEventDetailDrawerSignature {
  Args: {
    event: DetailEvent;
    onClose: () => void;
    relatedEventsLoading: boolean;
    relatedEventsCount: number;
    onOpenRelatedDrawer: () => void;
  };
}

export default <template>
  <TableDrawer>
    {{! Header }}
    <div class="flex justify-between items-start p-6 border-b border-gray-100">
      <div>
        <p class="text-xs text-carbon-40 mb-1">{{@event.processedDateDisplay}}
          &bull;
          {{@event.processedTimeDisplay}}</p>
        <h3 class="text-base font-semibold mb-0.5">{{@event.integrationName}}</h3>
        <p class="text-sm text-carbon-50 mb-0">{{if @event.locationName @event.locationName "–"}}</p>
      </div>
      <button type="button" class="text-carbon-40 hover:text-carbon-70 mt-1" {{on "click" @onClose}}>
        {{svgJar "cross-black" height="16" width="16"}}
      </button>
    </div>

    {{! Status + duration }}
    <div class="flex gap-4 px-6 py-4 border-b border-gray-100">
      <div class="flex-1">
        <p class="text-xs text-carbon-40 mb-1">Status</p>
        {{#if (eq @event.operationStatus "FAILED")}}
          {{#if (eq @event.failureType "TIMEOUT")}}
            <span class="flex items-center gap-1 text-yellow-600 font-medium text-sm">
              <div class="shrink-0 w-4 h-4">{{svgJar "caution-triangle" height="14" width="14"}}</div>
              Timeout
            </span>
          {{else}}
            <span class="flex items-center gap-1 text-red-600 font-medium text-sm">
              <div class="shrink-0 w-4 h-4">{{svgJar "circle-cross-red" height="14" width="14"}}</div>
              Failed
            </span>
          {{/if}}
        {{else if (eq @event.operationStatus "PARTIAL")}}
          <span class="flex items-center gap-1 text-yellow-600 font-medium text-sm">
            <div class="shrink-0 w-4 h-4">{{svgJar "caution-triangle" height="14" width="14"}}</div>
            Partial
          </span>
        {{else}}
          <span class="flex items-center gap-1 text-green-700 font-medium text-sm">
            <div class="shrink-0 w-4 h-4">{{svgJar "checkmark-circle-green" height="14" width="14"}}</div>
            Success
          </span>
        {{/if}}
      </div>
      <div class="flex-1">
        <p class="text-xs text-carbon-40 mb-1">Duration</p>
        <p class="text-sm text-carbon-70 mb-0">{{@event.durationMillis}}ms</p>
      </div>
    </div>

    {{! Operation details }}
    <div class="px-6 py-4 border-b border-gray-100">
      <p class="text-xs font-medium text-carbon-40 mb-3">Operation</p>
      <div class="flex flex-col gap-3">
        <div>
          <p class="text-xs text-carbon-40 mb-0.5">Type</p>
          <p class="text-sm text-carbon-70 mb-0">{{@event.operationTypeLabel}}</p>
        </div>
        {{#if @event.operationDefinition}}
          <div>
            <p class="text-xs text-carbon-40 mb-0.5">Definition</p>
            <p class="text-sm text-carbon-70 mb-0">{{@event.operationDefinition}}</p>
          </div>
        {{/if}}
        <div>
          <p class="text-xs text-carbon-40 mb-0.5">Direction</p>
          <p class="text-sm text-carbon-70 mb-0">{{@event.direction}}</p>
        </div>
        {{#if @event.endpoint}}
          <div>
            <p class="text-xs text-carbon-40 mb-0.5">Endpoint</p>
            <p class="text-sm text-carbon-70 mb-0 font-mono">{{@event.endpoint}}</p>
          </div>
        {{/if}}
        {{#if @event.primaryMetric}}
          <div>
            <p class="text-xs text-carbon-40 mb-0.5">Primary Metric</p>
            <p class="text-sm text-carbon-70 mb-0">{{@event.primaryMetric}}</p>
          </div>
        {{/if}}
      </div>
    </div>

    {{! Failure details }}
    {{#if (eq @event.operationStatus "FAILED")}}
      <div class="px-6 py-4">
        <p class="text-xs font-medium text-carbon-40 mb-3">Failure Details</p>
        <div class="flex flex-col gap-3">
          {{#if @event.failureType}}
            <div>
              <p class="text-xs text-carbon-40 mb-0.5">Failure Type</p>
              <p class="text-sm text-carbon-70 mb-0">{{@event.failureType}}</p>
            </div>
          {{/if}}
          {{#if @event.responsibleParty}}
            <div>
              <p class="text-xs text-carbon-40 mb-0.5">Responsible Party</p>
              <p class="text-sm text-carbon-70 mb-0">{{@event.responsibleParty}}</p>
            </div>
          {{/if}}
          {{#if @event.failureReason}}
            <div>
              <p class="text-xs text-carbon-40 mb-0.5">Reason</p>
              <p class="text-sm text-carbon-70 mb-0">{{@event.failureReason}}</p>
            </div>
          {{/if}}
          {{#if @event.errorDetail}}
            <div>
              <p class="text-xs text-carbon-40 mb-0.5">Error Detail</p>
              <p class="text-sm text-carbon-70 mb-0 font-mono">{{@event.errorDetail}}</p>
            </div>
          {{/if}}
          {{#if @event.displayReason}}
            <div>
              <p class="text-xs text-carbon-40 mb-0.5">Summary</p>
              <p class="text-sm text-carbon-70 mb-0">{{@event.displayReason}}</p>
            </div>
          {{/if}}
          {{#if @event.recommendedAction}}
            <div>
              <p class="text-xs text-carbon-40 mb-0.5">Recommended Action</p>
              <p class="text-sm text-carbon-70 mb-0">{{@event.recommendedAction}}</p>
            </div>
          {{/if}}
          {{#if @event.helpCenterUrl}}
            <div>
              <a
                href={{@event.helpCenterUrl}}
                target="_blank"
                rel="noopener noreferrer"
                class="text-sm text-blue-600 hover:underline"
              >
                View Help Center article
              </a>
            </div>
          {{/if}}
        </div>
      </div>
    {{/if}}

    {{! Trace IDs }}
    <div class="px-6 py-4 border-t border-gray-100">
      <p class="text-xs font-medium text-carbon-40 mb-3">Identifiers</p>
      <div class="flex flex-col gap-3">
        <div>
          <p class="text-xs text-carbon-40 mb-0.5">Event ID</p>
          <p class="text-xs text-carbon-70 mb-0 font-mono break-all">{{@event.eventId}}</p>
        </div>
        {{#if @event.correlationId}}
          <div>
            <p class="text-xs text-carbon-40 mb-0.5">Correlation ID</p>
            <p class="text-xs text-carbon-70 mb-0 font-mono break-all">{{@event.correlationId}}</p>
          </div>
        {{/if}}
      </div>
    </div>

    {{! Related Events }}
    {{#if @event.correlationId}}
      <div class="px-6 py-4 border-t border-gray-100">
        <p class="text-xs font-medium text-carbon-40 mb-3">Related Events</p>
        {{#if @relatedEventsLoading}}
          <p class="text-xs text-carbon-40">Loading…</p>
        {{else if (gt @relatedEventsCount 0)}}
          <button
            type="button"
            class="flex items-center justify-between w-full text-left text-sm text-carbon-70 hover:text-carbon-90 group"
            {{on "click" @onOpenRelatedDrawer}}
          >
            <span>{{@relatedEventsCount}} related event{{if (gt @relatedEventsCount 1) "s"}}</span>
            <span class="text-carbon-40 group-hover:text-carbon-70 text-base leading-none">›</span>
          </button>
        {{else}}
          <p class="text-xs text-carbon-40">No related events</p>
        {{/if}}
      </div>
    {{/if}}
  </TableDrawer>
</template> satisfies TOC<HealthDashboardEventDetailDrawerSignature>;
